/**
 * Inventory, equipment, and consumable operations — pure state mutations.
 *
 * UI dispatches these; it never edits state directly. Equipment is per-hero:
 * each hero owns an 8-slot `equipped` set. Foods and revive kits stack by
 * id/count in the shared `state.consumables`. Equipping an item swaps whatever
 * occupied the slot back into the shared bag (`state.inventory`).
 */

/** Map an item's slot to a concrete equipped key on a hero (rings have two). */
function resolveSlot(hero, item) {
  if (item.slot === 'ring') {
    return hero.equipped.ring1 ? (hero.equipped.ring2 ? 'ring1' : 'ring2') : 'ring1';
  }
  return item.slot;
}

/**
 * Add an equipment instance to the shared bag.
 * @param {object} state
 * @param {object} item
 */
export function addEquipment(state, item) {
  state.inventory.push(item);
}

/**
 * Add food to the consumable stash.
 * @param {object} state
 * @param {string} foodId
 * @param {number} [qty=1]
 */
export function addFood(state, foodId, qty = 1) {
  state.consumables.foods[foodId] = (state.consumables.foods[foodId] ?? 0) + qty;
}

/**
 * Add revive kits.
 * @param {object} state
 * @param {number} [n=1]
 */
export function addReviveKit(state, n = 1) {
  state.consumables.reviveKits = (state.consumables.reviveKits ?? 0) + n;
}

/**
 * Add protection amulets (consumed on dungeon entry for -30% incoming dmg).
 * @param {object} state
 * @param {number} [n=1]
 */
export function addProtectionAmulet(state, n = 1) {
  state.consumables.protectionAmulets = (state.consumables.protectionAmulets ?? 0) + n;
}

/**
 * Whether a hero's class may equip an item. Two independent gates:
 *   - Weapons are class-locked by weapon TYPE: each class wields one kind
 *     (knight→sword, rogue→dagger, ranger→bow, pyromancer→staff, priest→wand).
 *   - Any item may carry an explicit `classes` allow-list (class-signature gear).
 * Armor/accessories without a `classes` list are universal.
 * @param {object} hero
 * @param {object} item
 * @param {object} [classes] the classes data table (for weapon-type lookup)
 * @returns {boolean}
 */
export function canEquip(hero, item, classes) {
  if (!item) return false;
  // Explicit per-item allow-list always wins.
  if (Array.isArray(item.classes) && item.classes.length) {
    if (!item.classes.includes(hero.classId)) return false;
  }
  // Weapons must match the class's weapon type.
  if (item.slot === 'weapon' && item.weapon) {
    const want = classes?.[hero.classId]?.weapon;
    if (want && item.weapon !== want) return false;
  }
  return true;
}

/**
 * Equip an inventory item (by instance id) onto a specific hero; returns the
 * previously equipped item (now back in the shared bag) or null. Rejects (and
 * returns null) if the hero's class cannot use the item.
 * @param {object} state
 * @param {object} hero  the hero receiving the item
 * @param {string} itemId
 * @param {object} [classes] classes data table for class-restriction checks
 * @returns {object|null}
 */
export function equip(state, hero, itemId, classes) {
  const idx = state.inventory.findIndex((i) => i.id === itemId);
  if (idx === -1) return null;
  const item = state.inventory[idx];
  if (item.kind !== 'equipment') return null;
  if (classes && !canEquip(hero, item, classes)) return null;

  const slot = resolveSlot(hero, item);
  const prev = hero.equipped[slot] ?? null;
  hero.equipped[slot] = item;
  state.inventory.splice(idx, 1);
  if (prev) state.inventory.push(prev);
  return prev;
}

/**
 * Unequip the item in a hero's slot back to the shared bag.
 * @param {object} state
 * @param {object} hero
 * @param {string} slot
 * @returns {object|null}
 */
export function unequip(state, hero, slot) {
  const item = hero.equipped[slot];
  if (!item) return null;
  hero.equipped[slot] = null;
  state.inventory.push(item);
  return item;
}

/**
 * Auto-eat the best-value food for a hero below the heal threshold.
 * @param {object} state
 * @param {object} hero
 * @param {{loot:object, balance:object}} data
 * @returns {{foodId:string, heal:number}|null}
 */
export function autoEat(state, hero, data) {
  if (!hero.alive) return null;
  const threshold = data.balance.food.autoEatBelowPct;
  if (hero.hp / hero.maxHp > threshold) return null;

  const owned = Object.entries(state.consumables.foods).filter(([, q]) => q > 0);
  if (!owned.length) return null;

  // Prefer the strongest heal available.
  owned.sort((a, b) => (data.loot.foods[b[0]].healPct - data.loot.foods[a[0]].healPct));
  const [foodId] = owned[0];
  const heal = Math.round(hero.maxHp * data.loot.foods[foodId].healPct);
  hero.hp = Math.min(hero.maxHp, hero.hp + heal);
  state.consumables.foods[foodId] -= 1;
  return { foodId, heal };
}

/**
 * Spend a revive kit to instantly revive a specific downed hero, if available.
 * @param {object} state
 * @param {object} hero
 * @returns {boolean} true if a kit was used
 */
export function useReviveKit(state, hero) {
  if ((state.consumables.reviveKits ?? 0) <= 0) return false;
  state.consumables.reviveKits -= 1;
  hero.alive = true;
  hero.hp = hero.maxHp;
  hero.reviveAt = 0;
  hero.lockedUntil = 0;
  return true;
}

/**
 * Whether a hero is in a death lockout (cannot fight yet).
 * @param {object} hero
 * @returns {boolean}
 */
export function isLocked(hero) {
  return (hero?.lockedUntil ?? 0) > Date.now();
}

/**
 * Whether at least one hero in the party is playable (not locked).
 * @param {object} state
 * @returns {boolean}
 */
export function partyPlayable(state) {
  return (state.party ?? []).some((h) => !isLocked(h));
}

/**
 * Remaining death-lockout for a hero, in milliseconds (0 if free to play).
 * @param {object} hero
 * @returns {number}
 */
export function lockRemainingMs(hero) {
  return Math.max(0, (hero?.lockedUntil ?? 0) - Date.now());
}

/**
 * Remaining daily-dungeon cooldown, in milliseconds (0 if ready now).
 * @param {object} state
 * @returns {number}
 */
export function dungeonRemainingMs(state) {
  return Math.max(0, (state?.dungeon?.availableAt ?? 0) - Date.now());
}

/**
 * Format a remaining-lockout duration (ms) as a compact countdown that always
 * surfaces minutes: "5h 23m", "47m", or "<1m" in the final seconds. Returns ''
 * for non-positive durations.
 * @param {number} ms
 * @returns {string}
 */
export function formatLockTime(ms) {
  if (!ms || ms <= 0) return '';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return '<1m';
}

// ---------------------------------------------------------------------------
// Shop — spend in-game gold on consumables.
// ---------------------------------------------------------------------------

/**
 * Buy `qty` of a food with gold. No-op (returns false) if unaffordable.
 * @param {object} state
 * @param {{loot:object}} data
 * @param {string} foodId
 * @param {number} [qty=1]
 * @returns {boolean} true if the purchase went through
 */
export function buyFood(state, data, foodId, qty = 1) {
  const food = data.loot.foods[foodId];
  if (!food || qty <= 0) return false;
  const cost = (food.price ?? 0) * qty;
  if (cost <= 0 || (state.player.gold ?? 0) < cost) return false;
  state.player.gold -= cost;
  addFood(state, foodId, qty);
  return true;
}

/**
 * Buy `n` revive kits with gold. No-op (returns false) if unaffordable.
 * @param {object} state
 * @param {{loot:object}} data
 * @param {number} [n=1]
 * @returns {boolean} true if the purchase went through
 */
export function buyReviveKit(state, data, n = 1) {
  if (n <= 0) return false;
  const cost = (data.loot.reviveKit?.price ?? 0) * n;
  if (cost <= 0 || (state.player.gold ?? 0) < cost) return false;
  state.player.gold -= cost;
  addReviveKit(state, n);
  return true;
}

/**
 * Buy `n` protection amulets with gold. No-op (returns false) if unaffordable.
 * @param {object} state
 * @param {{loot:object}} data
 * @param {number} [n=1]
 * @returns {boolean} true if the purchase went through
 */
export function buyProtectionAmulet(state, data, n = 1) {
  if (n <= 0) return false;
  const cost = (data.loot.protectionAmulet?.price ?? 0) * n;
  if (cost <= 0 || (state.player.gold ?? 0) < cost) return false;
  state.player.gold -= cost;
  addProtectionAmulet(state, n);
  return true;
}
